import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ComuneSelect } from "../../components/ComuneSelect";
import { toast } from "sonner";

const emptyAgent = { name: "", phone: "", email: "" };

export default function AgenziaProfilo() {
  const { agency: authAgency, user } = useAuth();
  const [ag, setAg] = useState(authAgency || null);

  // Cambio password personale
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const load = () => api.get("/agency/profile").then((r) => setAg(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = {
        name: ag.name, comune: ag.comune, provincia: ag.provincia,
        website: ag.website, agents: (ag.agents || []).filter((a) => (a.name || "").trim())
      };
      await api.patch("/agency/profile", body);
      toast.success("Profilo aggiornato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadLogo = async (file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/agency/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAg({ ...ag, logo: data.logo });
      toast.success("Logo aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setAgent = (idx, patch) => {
    const agents = [...(ag.agents || [])];
    agents[idx] = { ...agents[idx], ...patch };
    setAg({ ...ag, agents });
  };
  const addAgent = () => setAg({ ...ag, agents: [...(ag.agents || []), { ...emptyAgent }] });
  const removeAgent = (idx) => setAg({ ...ag, agents: (ag.agents || []).filter((_, i) => i !== idx) });

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwNew.length < 8) { toast.error("La nuova password deve avere almeno 8 caratteri"); return; }
    if (pwNew !== pwConfirm) { toast.error("Le due password non coincidono"); return; }
    if (pwNew === pwCurrent) { toast.error("La nuova password deve essere diversa da quella attuale"); return; }
    setPwLoading(true);
    try {
      await api.post("/agency/me/password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password aggiornata");
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore cambio password");
    } finally { setPwLoading(false); }
  };

  if (!ag) return <div className="text-[#5C5657]">Caricamento…</div>;
  const logoUrl = ag.logo && !ag.logo.startsWith("http") ? `${process.env.REACT_APP_BACKEND_URL}${ag.logo}` : ag.logo;

  return (
    <div data-testid="agenzia-profilo">
      <div className="label-uppercase">La mia agenzia</div>
      <h1 className="font-serif text-4xl mt-2 mb-8">Profilo agenzia</h1>

      <div className="bg-white border border-[#E6E4E0] p-8 space-y-8">
        {/* Logo */}
        <div className="flex items-start gap-6">
          <div className="w-32 h-32 bg-[#F4F3F1] border border-[#E6E4E0] flex items-center justify-center overflow-hidden">
            {logoUrl ? <img src={logoUrl} alt="logo" className="w-full h-full object-contain" /> : <span className="text-xs text-[#5C5657]">Nessun logo</span>}
          </div>
          <div>
            <div className="label-uppercase">Logo agenzia</div>
            <div className="text-xs text-[#5C5657] mt-1 mb-3">PNG, JPG, WEBP o SVG · max 5MB · sfondo trasparente consigliato</div>
            <label className="text-xs label-uppercase border border-[#E6E4E0] px-3 py-2 cursor-pointer hover:border-[#7D2E3F]">
              Carica logo
              <input type="file" accept="image/*" data-testid="profilo-logo-input" className="hidden"
                     onChange={(e) => { if (e.target.files?.[0]) uploadLogo(e.target.files[0]); e.target.value=""; }} />
            </label>
          </div>
        </div>

        {/* Anagrafica */}
        <div className="grid grid-cols-2 gap-4 border-t border-[#E6E4E0] pt-6">
          <div><label className="label-uppercase">Nome agenzia</label>
            <input value={ag.name || ""} onChange={(e) => setAg({...ag, name: e.target.value})} className="admin-input" data-testid="profilo-name" /></div>
          <div><label className="label-uppercase">Sito web</label>
            <input value={ag.website || ""} onChange={(e) => setAg({...ag, website: e.target.value})} className="admin-input" data-testid="profilo-website" /></div>
          <div><label className="label-uppercase">Comune</label>
            <ComuneSelect
              value={ag.comune}
              onChange={(v) => setAg({ ...ag, comune: v })}
              provincia={ag.provincia || null}
              className="admin-input"
              testid="profilo-comune"
            />
          </div>
          <div><label className="label-uppercase">Provincia</label>
            <input value={ag.provincia || ""} onChange={(e) => setAg({...ag, provincia: e.target.value})} className="admin-input" data-testid="profilo-provincia" /></div>
        </div>

        {/* Agenti */}
        <div className="border-t border-[#E6E4E0] pt-6">
          <div className="flex justify-between items-center mb-4">
            <div className="label-uppercase text-[#7D2E3F]">Collaboratori</div>
            <button onClick={addAgent} data-testid="profilo-add-agent" className="text-xs label-uppercase text-[#7D2E3F] hover:underline">+ Aggiungi</button>
          </div>
          <div className="space-y-3">
            {(ag.agents || []).map((a, idx) => (
              <div key={idx} data-testid={`profilo-agent-${idx}`} className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-4"><label className="label-uppercase">Nome</label>
                  <input value={a.name || ""} onChange={(e) => setAgent(idx, {name: e.target.value})} className="admin-input" /></div>
                <div className="col-span-3"><label className="label-uppercase">Telefono</label>
                  <input value={a.phone || ""} onChange={(e) => setAgent(idx, {phone: e.target.value})} className="admin-input" /></div>
                <div className="col-span-4"><label className="label-uppercase">Email</label>
                  <input value={a.email || ""} onChange={(e) => setAgent(idx, {email: e.target.value})} className="admin-input" /></div>
                <div className="col-span-1 text-right">
                  <button onClick={() => removeAgent(idx)} className="text-xs label-uppercase text-[#B3261E]">×</button>
                </div>
              </div>
            ))}
            {(ag.agents || []).length === 0 && <div className="text-xs text-[#5C5657]">Nessun collaboratore registrato</div>}
          </div>
        </div>

        <div className="border-t border-[#E6E4E0] pt-6 text-right">
          <button data-testid="profilo-save" onClick={save} className="btn-primary">Salva modifiche</button>
        </div>
      </div>

      {/* Sezione: cambio password personale */}
      <div className="mt-8 bg-white border border-[#E6E4E0] p-8" data-testid="profilo-password-card">
        <div className="label-uppercase text-[#7D2E3F]">Il mio accesso</div>
        <h2 className="font-serif text-2xl mt-2 mb-2">Cambia la tua password</h2>
        <p className="text-xs text-[#5C5657] mb-6 leading-relaxed max-w-2xl">
          {user?.email ? <>Accesso: <strong>{user.email}</strong> — </> : null}
          l&apos;email di login è gestita da RX CASA. Da qui puoi cambiare la tua password personale.
          Se l&apos;hai dimenticata, dalla pagina di login usa &quot;Password dimenticata?&quot; per ricevere un link via email.
        </p>
        <form onSubmit={changePassword} className="grid md:grid-cols-3 gap-4 max-w-4xl">
          <div>
            <label className="label-uppercase">Password attuale</label>
            <input type="password" required minLength={8} value={pwCurrent}
                   data-testid="profilo-pw-current"
                   onChange={(e) => setPwCurrent(e.target.value)}
                   className="admin-input" autoComplete="current-password" />
          </div>
          <div>
            <label className="label-uppercase">Nuova password</label>
            <input type="password" required minLength={8} value={pwNew}
                   data-testid="profilo-pw-new"
                   onChange={(e) => setPwNew(e.target.value)}
                   className="admin-input" autoComplete="new-password"
                   placeholder="min. 8 caratteri" />
          </div>
          <div>
            <label className="label-uppercase">Conferma nuova</label>
            <input type="password" required minLength={8} value={pwConfirm}
                   data-testid="profilo-pw-confirm"
                   onChange={(e) => setPwConfirm(e.target.value)}
                   className="admin-input" autoComplete="new-password" />
          </div>
          <div className="md:col-span-3 text-right">
            <button type="submit" disabled={pwLoading} data-testid="profilo-pw-submit" className="btn-primary">
              {pwLoading ? "Salvo…" : "Aggiorna password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
